import {
  buildGeolocationGetRequest,
  fetchFunction
} from "../../ssc-api/useFetchApi";
import { useGeolocation } from "../../utils/localization/geolocation/GeolocationContext";
import { useDateOverride } from "src/utils/date/useDateOverride";
import {
  CountryPaymentTermsApiRequest,
  CountryPaymentTermsApiResponse
} from "@silversea-ssc/dotcom-api-types/CountryPaymentTerms";
import {
  CruisePaymentTermsApiRequest,
  CruisePaymentTermsApiResponse
} from "@silversea-ssc/dotcom-api-types/CruisePaymentTerms";
import type { AdministrativeFee as _AdministrativeFee } from "@silversea-ssc/dotcom-read-model-types/CruisePaymentTerms";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { FareCode } from "@silversea-ssc/web-fares";
import { useQueries, UseQueryResult } from "@tanstack/react-query";
import { isPresent } from "src/utils/checks";
import { useQuerySearch } from "src/utils/useQueryParams";

export type AdministrativeFee = _AdministrativeFee;
export type CountryPaymentTermsResponse = CountryPaymentTermsApiResponse;
type CruisePaymentTermsResponse = CruisePaymentTermsApiResponse;

export type PaymentTermsResponse =
  | CountryPaymentTermsResponse
  | CruisePaymentTermsResponse;

export const useRemotePaymentTermsPerFares = (fareCodes: FareCode[]) => {
  const geolocation = useGeolocation();
  const language = useLanguage();
  const querySearch = useQuerySearch();

  const queries = fareCodes.map((fareCode) => {
    const request =
      geolocation.countryCode && geolocation.defaultCurrency
        ? buildGeolocationGetRequest<CountryPaymentTermsApiRequest>(
            `countryPaymentTerms`,
            {
              fareCode: fareCode,
              currency: geolocation.defaultCurrency
            },
            querySearch,
            language
          )
        : undefined;

    return {
      queryKey: [
        "countryPaymentTerms",
        fareCode,
        geolocation.countryCode,
        language.code
      ],
      retry: false,
      enabled: isPresent(request),
      queryFn: fetchFunction<CountryPaymentTermsApiResponse>(request)
    };
  });
  const allFareQueries = useQueries({
    queries: queries
  });
  return allFareQueries;
};

export const useRemotePaymentTermsPerCruise = (
  fareCodes: FareCode[],
  cruiseCode: string,
  enable: boolean
) => {
  const geolocation = useGeolocation();
  const dateOverride = useDateOverride();
  const language = useLanguage();
  const querySearch = useQuerySearch();

  const queries = fareCodes.map((fareCode) => {
    const request =
      geolocation.defaultCurrency && enable
        ? buildGeolocationGetRequest<CruisePaymentTermsApiRequest>(
            `cruisePaymentTerms`,
            {
              fareCode,
              cruiseCode: cruiseCode,
              currency: geolocation.defaultCurrency,
              date: dateOverride ? dateOverride.toISOString() : undefined
            },
            querySearch,
            language
          )
        : undefined;
    return {
      queryKey: [
        "cruisePaymentTerms",
        cruiseCode,
        fareCode,
        geolocation.defaultCurrency,
        language.code
      ],
      retry: true,
      enabled: isPresent(request),
      queryFn: fetchFunction<CruisePaymentTermsApiResponse>(request)
    };
  });

  const allQueries = useQueries({
    queries: queries
  });

  return allQueries;
};

export const reduceToPaymentTermsApiResponse = <T>(
  results: UseQueryResult<T | undefined, Error>[]
): T[] => results.map((r) => r.data).filter(isPresent);
